import React, { useState } from 'react';
import { supabase } from './supabase';
import { X } from 'lucide-react';

// ── Alta AUTOMÁTICA de un cliente (provisioning, incrementos 1 y 2) ─────────────────
// Deja listo al cliente: proyecto Supabase → esquema → almacenamiento → catálogos →
// verificación → usuario master → ficha en el portal → plan aplicado → sitio en Cloudflare.
//
// Se puede RETOMAR: con "usar un proyecto existente" y su ref, cada paso que ya esté
// hecho se salta (el servidor lo dice con `ya`). El servidor nunca carga el esquema
// sobre una base que no esté vacía, ni sobre el proyecto de un cliente registrado.

async function prov(accion, extra = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch('/api/provision', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
    body: JSON.stringify({ accion, ...extra }),
  });
  const texto = await r.text();
  let res = null;
  try { res = texto ? JSON.parse(texto) : null; } catch { /* no-JSON */ }
  if (!res) throw new Error(`El servicio de provisioning no respondió correctamente (${r.status}).`);
  if (!r.ok) throw new Error(res.error || `Error ${r.status}`);
  return res;
}

// Fin de la prueba gratis: 3 meses calendario desde hoy (fecha de Panamá). Al día
// siguiente su sistema avisa que la licencia venció y da 5 días de gracia.
const MESES_PRUEBA = 3;
function finDePrueba() {
  const hoy = new Date(Date.now() - 5 * 3600 * 1000);
  const d = new Date(Date.UTC(hoy.getUTCFullYear(), hoy.getUTCMonth() + MESES_PRUEBA, hoy.getUTCDate()));
  return d.toISOString().slice(0, 10);
}

const sleep = (ms) => new Promise(res => setTimeout(res, ms));
// 48 + "corredata-" = 58, el máximo de un nombre de proyecto de Pages.
const slugDe = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48).replace(/-+$/, '');

export default function Alta({ planes, actor, onClose, onHecho, logEvento }) {
  const [f, setF] = useState({ nombre: '', plan_id: '', contacto_nombre: '', email: '', usuario: '', modo: 'nuevo', ref: '' });
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [final, setFinal] = useState(null);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const paso = (txt, estado = 'ok') => setLog(l => [...l, { txt, estado }]);

  const correr = async () => {
    const nombre = f.nombre.trim();
    if (!nombre || !f.plan_id || !f.email.trim() || !f.usuario.trim() || !f.contacto_nombre.trim()) { setErr('Llena todos los campos.'); return; }
    if (f.modo === 'existente' && !/^[a-z0-9]{20}$/.test(f.ref.trim())) { setErr('El ref del proyecto son 20 letras/números.'); return; }
    if (f.modo === 'nuevo' && !window.confirm(`Se creará un proyecto Supabase REAL para "${nombre}". Cuesta unos US$10 al mes desde que existe. ¿Continuar?`)) return;
    setBusy(true); setErr(''); setLog([]); setFinal(null);
    const res = { nombre };
    try {
      // 1. Proyecto
      let ref = f.ref.trim();
      if (f.modo === 'nuevo') {
        paso('Creando el proyecto en Supabase…', 'run');
        ref = (await prov('crear_proyecto', { nombre: `corredata-${slugDe(nombre)}` })).ref;
      }
      res.ref = ref;
      let st = null;
      for (let i = 0; i < 40; i++) {
        st = await prov('estado_proyecto', { ref });
        if (st.listo) break;
        paso(`Proyecto ${ref}: ${st.status}, esperando…`, 'run');
        await sleep(10000);
      }
      if (!st?.listo || !st.anon) throw new Error('El proyecto no quedó listo o no entregó sus llaves.');
      paso(`Proyecto ${ref} listo`);
      res.url = st.url;

      // ¿Ya está registrado en el portal? Entonces la base ya se hizo: solo falta el plan.
      const { data: reg } = await supabase.from('clientes').select('id, slug, app_url').eq('supabase_ref', ref).maybeSingle();
      let clienteId = reg?.id || null;
      const slug = reg?.slug || slugDe(nombre);

      if (!clienteId) {
        // 2. Plantilla
        for (const [p, que] of [['esquema', 'Esquema'], ['storage', 'Almacenamiento de documentos'], ['catalogos', 'Catálogos']]) {
          paso(`${que}: cargando…`, 'run');
          const r = await prov('cargar_plantilla', { ref, paso: p });
          if (!r.ok && !r.ya) throw new Error(r.error);
          paso(r.ya ? `${que}: ya estaba` : `${que}: cargado`);
        }
        // 3. Verificación
        const v = await prov('verificar', { ref });
        if (!v.ok) throw new Error(v.error);
        const c = v.conteo || {};
        paso(`Verificado: ${c.tablas} tablas · ${c.politicas} políticas · ${c.funciones} funciones · ${c.aseguradoras} aseguradoras · ${c.ramos} ramos · ${c.coberturas} coberturas · bucket ${c.bucket_publico === false ? 'privado' : '¿público?'}`);
        if (!c.roles_master || !c.cont_config || c.bucket_publico !== false) throw new Error('La verificación no cuadra (falta el rol master, cont_config o el bucket privado).');

        // 4. Master
        paso('Creando el usuario master…', 'run');
        const m = await prov('crear_master', { ref, nombre: f.contacto_nombre.trim(), email: f.email.trim(), usuario: f.usuario.trim() });
        if (!m.ok && !m.ya) throw new Error(m.error);
        if (m.ya) paso('Master: ya existía (su clave temporal se entregó cuando se creó)');
        else {
          paso(`Master creado: ${m.usuario}`); res.master = m;
          paso(`Registro público: ${m.registro_publico || 'sin confirmar'}`, m.registro_publico === 'apagado' ? 'ok' : 'error');
        }

        // 5. Ficha en el portal (en prueba: aún no se cobra). La IA y su cupo salen del plan,
        // igual que cuando el master cambia de plan en "Mi Plan": nadie tiene que prenderla.
        const plan = planes.find(p => String(p.id) === String(f.plan_id)) || {};
        const { data: nuevo, error } = await supabase.from('clientes').insert({
          nombre, slug: slugDe(nombre) || null, plan_id: Number(f.plan_id), estado: 'prueba',
          con_ia: !!plan.con_ia, limite_ia_mes: plan.limite_ia_mes ?? null,
          prueba_hasta: finDePrueba(),
          supabase_ref: ref, anon_key: st.anon, contacto_nombre: f.contacto_nombre.trim(), contacto_email: f.email.trim(),
        }).select('id').single();
        if (error) throw new Error('No se pudo registrar el cliente en el portal: ' + error.message);
        clienteId = nuevo.id;
        await logEvento(clienteId, 'alta', `${nombre} — alta automática (proyecto ${ref})`, actor);
        paso('Registrado en el portal (en prueba)');
      } else {
        paso('Ya estaba registrado en el portal: se salta la carga y el master');
      }

      // 6. Plan
      paso('Aplicando el plan a la instancia…', 'run');
      const { data: { session } } = await supabase.auth.getSession();
      const ap = await fetch('/api/plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ accion: 'aplicar', cliente_id: clienteId }),
      }).then(r => r.json()).catch(e => ({ ok: false, error: e.message }));
      if (!ap.ok) throw new Error('El plan no se aplicó: ' + (ap.error || 'error desconocido'));
      paso(`Plan ${ap.resuelto?.plan} aplicado`);

      // 7. Sitio en Cloudflare (incremento 3) + su primer despliegue.
      if (reg?.app_url) {
        paso(`Sitio: ya tenía (${reg.app_url})`);
        res.app_url = reg.app_url;
      } else {
        const nombreSitio = `corredata-${slug}`;
        paso(`Creando el sitio ${nombreSitio} en Cloudflare…`, 'run');
        const s = await prov('crear_sitio', { ref, nombre_sitio: nombreSitio });
        if (!s.ok) throw new Error(s.error);
        if (s.primer_despliegue?.error) paso(`Aviso: no se pudo lanzar el primer despliegue (${s.primer_despliegue.error}); se compilará con el próximo push a main`, 'error');
        paso(s.ya ? `Sitio: ya existía (${s.nombre})` : `Sitio creado: ${s.url}`);
        // Se sigue el despliegue: compilar tarda unos minutos (y Cloudflare los encola).
        let e = null;
        for (let i = 0; i < 60; i++) {
          e = await prov('estado_sitio', { nombre_sitio: nombreSitio });
          if (e.listo || e.fallo) break;
          if (i % 3 === 0) paso(`Compilando: ${e.despliegue ? `${e.despliegue.etapa} · ${e.despliegue.estado}` : 'en cola'}…`, 'run');
          await sleep(15000);
        }
        if (e?.fallo) throw new Error(`El despliegue falló en la etapa "${e.despliegue?.etapa}". Revisa el log del proyecto ${nombreSitio} en Cloudflare.`);
        const appUrl = e?.url || s.url;
        const { error: eu } = await supabase.from('clientes').update({ app_url: appUrl }).eq('id', clienteId);
        if (eu) throw new Error('El sitio existe, pero no se pudo anotar su URL en la ficha: ' + eu.message);
        res.app_url = appUrl;
        paso(e?.listo ? `Sitio en línea: ${appUrl}` : `Sitio creado; el despliegue sigue en curso (${appUrl})`, e?.listo ? 'ok' : 'run');
      }
      setFinal(res);
    } catch (e) {
      paso(e.message || String(e), 'error');
      setErr('Se detuvo. Corrige y vuelve a correrlo con "usar un proyecto existente" y el mismo ref: lo ya hecho se salta.' + (res.ref ? ` (ref ${res.ref})` : ''));
    } finally { setBusy(false); }
  };

  const color = { ok: 'var(--success)', run: 'var(--muted)', error: 'var(--danger)' };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid var(--line)' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Alta automática de un cliente</div>
            <div style={{ fontSize: 12, color: 'var(--faint)' }}>Deja lista su base, su master y su sitio en Cloudflare.</div>
          </div>
          <button className="btn btn-sm" onClick={onClose} disabled={busy}><X size={16} /></button>
        </div>

        <div style={{ padding: 22, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="lbl">Nombre del corretaje *</label>
            <input className="input" value={f.nombre} onChange={e => set('nombre', e.target.value)} disabled={busy} placeholder="Corretaje Ejemplo, S.A." />
          </div>
          <div>
            <label className="lbl">Plan *</label>
            <select className="input" value={f.plan_id} onChange={e => set('plan_id', e.target.value)} disabled={busy}>
              <option value="">— Elegir —</option>
              {planes.filter(p => p.activo !== false).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="lbl">Nombre del master (contacto) *</label>
            <input className="input" value={f.contacto_nombre} onChange={e => set('contacto_nombre', e.target.value)} disabled={busy} />
          </div>
          <div>
            <label className="lbl">Correo del master *</label>
            <input className="input" type="email" value={f.email} onChange={e => set('email', e.target.value)} disabled={busy} />
          </div>
          <div>
            <label className="lbl">Usuario del master *</label>
            <input className="input" value={f.usuario} onChange={e => set('usuario', e.target.value)} disabled={busy} placeholder="ej. jperez" />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
            <label><input type="radio" checked={f.modo === 'nuevo'} onChange={() => set('modo', 'nuevo')} disabled={busy} /> Crear un proyecto nuevo</label>
            <label><input type="radio" checked={f.modo === 'existente'} onChange={() => set('modo', 'existente')} disabled={busy} /> Usar un proyecto existente (vacío, o retomar)</label>
            {f.modo === 'existente' && <input className="input" style={{ flex: '1 1 200px' }} value={f.ref} onChange={e => set('ref', e.target.value.trim())} disabled={busy} placeholder="ref del proyecto" />}
          </div>
        </div>

        {log.length > 0 && (
          <div style={{ margin: '0 22px 14px', padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, display: 'grid', gap: 4 }}>
            {log.map((l, i) => <div key={i} style={{ color: color[l.estado] }}>{l.estado === 'ok' ? '✓' : l.estado === 'error' ? '✗' : '…'} {l.txt}</div>)}
          </div>
        )}
        {err && <div style={{ margin: '0 22px 14px', color: 'var(--danger)', fontSize: 13 }}>{err}</div>}

        {final && (
          <div style={{ margin: '0 22px 14px', padding: '12px 14px', borderRadius: 8, background: 'var(--success-bg)', fontSize: 13, display: 'grid', gap: 4 }}>
            <strong>Base lista para {final.nombre}.</strong>
            <div>Proyecto: {final.ref} · {final.url}</div>
            {final.master && <div>Master: <b>{final.master.usuario}</b> ({final.master.email}) · clave temporal <b style={{ fontFamily: 'monospace' }}>{final.master.clave_temporal}</b> — <u>anótala ahora: no se vuelve a mostrar</u>. Se le pedirá cambiarla al entrar.</div>}
            {final.app_url && <div>Su sistema: <a href={final.app_url} target="_blank" rel="noreferrer">{final.app_url}</a></div>}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '0 22px 20px' }}>
          {final
            ? <button className="btn btn-primary" onClick={onHecho}>Listo</button>
            : <button className="btn btn-primary" onClick={correr} disabled={busy}>{busy ? 'Trabajando…' : 'Dar de alta'}</button>}
        </div>
      </div>
    </div>
  );
}
